import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

import { useAtomValue } from 'jotai';

import { openModal } from '@/components/modals/modals.utils';
import { Button } from '@/components/ui/button';
import { UploadImage } from '@/components/ui/upload-image';
import { env } from '@/constants/env';

import { imageIdFromParamsAtom } from '../atoms/imageFiles';
import { CollapsableCard } from './collapsable-card';

interface ImageUploadProps {
  title?: string;
  estateId?: string;
  projectId?: string;
  onUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onCancelUpload?: () => void;
}

export const ImageUpload = ({
  title,
  estateId,
  projectId,
  onUpload,
  onCancelUpload,
}: ImageUploadProps) => {
  const imageId = useAtomValue(imageIdFromParamsAtom);
  const { t } = useTranslation();

  const handleTakePhoto = () => {
    if (!isMobile) {
      window.alert(t('noCameraError'));
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await onUpload(e);
    onCancelUpload?.();
  };

  const handleSelectFromMediaLibrary = () => {
    if (estateId) {
      openModal('media-library', { type: 'estate', estateId });
      onCancelUpload?.();
    } else if (projectId) {
      openModal('media-library', { type: 'project', projectId });
      onCancelUpload?.();
    }
  };

  const uploadOptionsContent = (
    <div className="flex flex-1 flex-col gap-1 sm:gap-2">
      <Button className="relative flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base">
        {t('upload')}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity hover:opacity-100">
          <UploadImage onUpload={handleUpload} hideIcon />
        </div>
      </Button>
      {isMobile ? (
        <Button
          className="hidden flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light sm:block lg:text-base"
          onClick={handleTakePhoto}
        >
          {t('createPhoto')}
        </Button>
      ) : null}
      {estateId || projectId ? (
        <Button
          className="!h-auto flex-1 whitespace-break-spaces border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base"
          onClick={handleSelectFromMediaLibrary}
        >
          {t('selectFromMediaLibrary')}
        </Button>
      ) : null}
      {onCancelUpload ? (
        <Button
          className="flex-1 border border-solid border-primary-light bg-white py-2 text-xs text-primary-light lg:text-base"
          onClick={onCancelUpload}
        >
          {t('cancel')}
        </Button>
      ) : null}
    </div>
  );

  return (
    <CollapsableCard title={title ?? t('newRenderingFromImage')}>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {imageId ? (
          <img
            className="block h-32 rounded-sm object-cover sm:hidden lg:h-48"
            src={`${env.API_URL}/images/${imageId}`}
            alt="Estate upload preview"
          />
        ) : null}
        {uploadOptionsContent}
      </div>
    </CollapsableCard>
  );
};
